import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState,
  type PropsWithChildren,
} from "react";
import { api, getApiErrorMessage } from "@/library/RequestMaker";
import { endpoints } from "@/library/Endpoints";

export type Group = {
  id: number;
  branch_id: number;
  branch_name: string;
  department_id: number;
  department_name: string;
  contract_id: number;
  contract_name: string;
  contract_start_date: string;
  contract_end_date: string;
  level_id: number;
  level_name: string;
  level_display_name: string;
  lesson_day_id: number;
  lesson_day_name: string;
  lesson_time_id: number;
  lesson_time_slot: string;
  lesson_time_start: string;
  lesson_time_end: string;
  teacher_id: number;
  teacher_name: string;
  teacher_phone: string;
  language_id: number;
  language_code: string;
  language_name: string;
  room_id: number | null;
  room_name: string | null;
  room_floor: number | null;
  room_number: string | null;
};

export type GroupHistoryItem = {
  student_id: number;
  person_id: number;
  group_id: number;
  join_date: string;
  move_date: string | null;
  leave_date: string | null;
  is_current: boolean;
  group: { id: number; branch_name: string } | null;
};

const ENGLISH_DEPARTMENT_PATTERN = /english|ingliz|англ/i;

/** Streak and Audios features are only available to English department students. */
export function isEnglishDepartment(group: Group | null | undefined) {
  return ENGLISH_DEPARTMENT_PATTERN.test(group?.department_name ?? "");
}

interface GroupContextValue {
  group: Group | null;
  isEnglish: boolean;
  history: GroupHistoryItem[];
  loading: boolean;
  error: string;
  hydrateGroup: (group: Group | null) => void;
  fetchGroup: () => Promise<void>;
  fetchHistory: () => Promise<void>;
}

const GroupContext = createContext<GroupContextValue | undefined>(undefined);

export function GroupProvider({ children }: PropsWithChildren) {
  const [group, setGroup] = useState<Group | null>(null);
  const [history, setHistory] = useState<GroupHistoryItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const _groupFetched = useRef(false);
  const _historyFetched = useRef(false);

  const hydrateGroup = useCallback((nextGroup: Group | null) => {
    setGroup(nextGroup);
    _groupFetched.current = Boolean(nextGroup);
  }, []);

  const fetchGroup = useCallback(async (force?: boolean) => {
    if (_groupFetched.current && !force) return;
    _groupFetched.current = true;
    setLoading(true);
    setError("");
    try {
      const { data } = await api.get(endpoints.group.current);
      if (data?.ok) {
        setGroup(data.group);
      }
    } catch (e: any) {
      _groupFetched.current = false;
      setError(getApiErrorMessage(e, "Failed to load group"));
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchHistory = useCallback(async (force?: boolean) => {
    if (_historyFetched.current && !force) return;
    _historyFetched.current = true;
    setLoading(true);
    setError("");
    try {
      const { data } = await api.get(endpoints.group.history);
      if (data?.ok) {
        setHistory(data.history ?? []);
      }
    } catch (e: any) {
      _historyFetched.current = false;
      setError(getApiErrorMessage(e, "Failed to load group history"));
    } finally {
      setLoading(false);
    }
  }, []);

  const value = useMemo<GroupContextValue>(
    () => ({
      group,
      isEnglish: isEnglishDepartment(group),
      history,
      loading,
      error,
      hydrateGroup,
      fetchGroup,
      fetchHistory,
    }),
    [group, history, loading, error, hydrateGroup, fetchGroup, fetchHistory],
  );

  return (
    <GroupContext.Provider value={value}>{children}</GroupContext.Provider>
  );
}

export function useGroup() {
  const ctx = useContext(GroupContext);
  if (!ctx) throw new Error("useGroup must be used within GroupProvider");
  return ctx;
}
